/**
 * Report Types
 * Types for report generation and viewing
 */

export enum ReportType {
  JAIL_VISITATION = 'F.11',
  OUTREACH_PROGRAM = 'F.10',
  OATH_ADMINISTERED = 'F.21',
  PSA_OATHS = 'F.22',
  VICTIMS_CASES = 'F.12',
  CICL_CASES = 'F.13',
  DETAINEE_RA10592 = 'F.14',
  DRUG_CASES = 'F.15',
  TAIWANESE_NATIONALS = 'F.16',
  MONTHLY_INVENTORY = 'F.17',
  MONTHLY_CASES_INVENTORY = 'F.19',
  NON_FILIPINOS = 'F.18',
  YEAR_END_INVENTORY = 'F.35',
}

export enum ExportFormat {
  JSON = 'json',
  PDF = 'pdf',
  EXCEL = 'excel',
}

export interface ReportTypeInfo {
  code: string
  name: string
  description: string
  category: string
  supports_export: boolean
  export_formats: ExportFormat[]
}

export interface ReportGenerateRequest {
  report_type: ReportType
  date_from: string  // ISO date string
  date_to: string    // ISO date string
  export_format?: ExportFormat
  page?: number
  limit?: number
}

export interface ReportResponse {
  report_type: string
  report_name: string
  date_from: string
  date_to: string
  generated_at: string
  total_records: number
  page: number
  pages: number
  limit: number
  items: Record<string, any>[]
}

export interface ReportFilters {
  report_type?: ReportType
  date_from?: string
  date_to?: string
}

// Report display names
export const REPORT_NAMES: Record<ReportType, string> = {
  [ReportType.JAIL_VISITATION]: 'Jail Visitation Report',
  [ReportType.OUTREACH_PROGRAM]: 'Outreach Program Report',
  [ReportType.OATH_ADMINISTERED]: 'Oaths Administered Report',
  [ReportType.PSA_OATHS]: 'PSA Oaths Administered Report',
  [ReportType.VICTIMS_CASES]: 'Victims Cases Handled',
  [ReportType.CICL_CASES]: 'CICL Cases Handled',
  [ReportType.DETAINEE_RA10592]: 'Detainee Waiver/Manifestation (R.A. 10592)',
  [ReportType.DRUG_CASES]: 'Clients Assisted on Drug Cases',
  [ReportType.TAIWANESE_NATIONALS]: 'Taiwanese Nationals Report',
  [ReportType.MONTHLY_INVENTORY]: 'Monthly Inventory of Clients Served',
  [ReportType.MONTHLY_CASES_INVENTORY]: 'Monthly Inventory of Cases',
  [ReportType.NON_FILIPINOS]: 'Non-Filipino Nationals Report',
  [ReportType.YEAR_END_INVENTORY]: 'Year-End Inventory of Clients Served',
}

// Report descriptions
export const REPORT_DESCRIPTIONS: Record<ReportType, string> = {
  [ReportType.JAIL_VISITATION]: 'Report of clients visited in detention facilities',
  [ReportType.OUTREACH_PROGRAM]: 'Report of outreach activities and beneficiaries',
  [ReportType.OATH_ADMINISTERED]: 'Report of oaths administered to clients',
  [ReportType.PSA_OATHS]: 'Report of PSA-related oaths administered',
  [ReportType.VICTIMS_CASES]: 'Report of cases handled for crime victims',
  [ReportType.CICL_CASES]: 'Report of Children in Conflict with the Law cases',
  [ReportType.DETAINEE_RA10592]: 'Detainee waiver/manifestation assistance under R.A. 10592',
  [ReportType.DRUG_CASES]: 'Clients assisted on drug cases by assistance type',
  [ReportType.TAIWANESE_NATIONALS]: 'Report of legal assistance provided to Taiwanese nationals',
  [ReportType.MONTHLY_INVENTORY]: 'Comprehensive monthly inventory of all clients served',
  [ReportType.MONTHLY_CASES_INVENTORY]: 'Monthly inventory of cases grouped by Criminal, Civil, Labor, and Administrative',
  [ReportType.NON_FILIPINOS]: 'Report of legal assistance provided to non-Filipino nationals',
  [ReportType.YEAR_END_INVENTORY]: 'Yearly inventory of all clients served',
}

// Report categories
export const REPORT_CATEGORIES: Record<string, ReportType[]> = {
  'Legal Services': [
    ReportType.JAIL_VISITATION,
    ReportType.VICTIMS_CASES,
    ReportType.CICL_CASES,
    ReportType.DETAINEE_RA10592,
    ReportType.DRUG_CASES,
    ReportType.MONTHLY_CASES_INVENTORY,
  ],
  'Notarial Services': [
    ReportType.OATH_ADMINISTERED,
    ReportType.PSA_OATHS,
  ],
  'Community Programs': [
    ReportType.OUTREACH_PROGRAM,
  ],
  'International Cases': [
    ReportType.TAIWANESE_NATIONALS,
    ReportType.NON_FILIPINOS,
  ],
  'Statistics': [
    ReportType.MONTHLY_INVENTORY,
    ReportType.YEAR_END_INVENTORY,
  ],
}
